import { V2, hoverOnly } from '../../../theme/tokens'
import { useEffect, useRef, useState } from 'react'
import { Box, Button } from '@mui/material'

/**
 * HoldButton — hold-to-confirm button (Pattern C).
 *
 * @description
 *   Sustained press (default 900ms) fills a progress bar; on completion fires
 *   onConfirm. Release early = clean cancel. Unified Pointer Events cover mouse
 *   + touch + pen. Keyboard fallback via Space/Enter held. Suppresses iOS
 *   long-press context menu via webkitTouchCallout + userSelect.
 *
 * @useWhen
 *   - Destructive action without an undo path (delete, wipe, permanent cancel)
 *   - Action where a modal feels too heavy but a plain click is too light
 *   - Any "this cannot be undone" copy in the surrounding dialog
 *
 * @dontUseFor
 *   - Non-destructive confirm → use InlineConfirmButton
 *   - Action with sub-second feedback loop where hold adds friction with no upside
 *   - Accessibility-critical paths where motor-impaired users must succeed
 *     (provide a fallback tap-to-confirm escape hatch)
 *
 * @related
 *   - InlineConfirmButton — lighter-weight morph confirm for reversible actions
 *   - ConfirmOnSelectDropdown — picker with consequence preview
 *
 * @reuseHints
 *   - "Clear all filters" buttons where bulk-reset is irreversible
 *   - Admin "Rotate API key" / "Revoke session" buttons
 *   - Discovery candidate "Reject forever" in the review queue
 *   - Bulk-delete action bar in any list view
 *
 * @props
 *   - holdMs — hold duration in ms (900 default; 600 for lighter, 1500 for nuclear)
 *   - label — idle state text ("Hold to delete")
 *   - labelActive — text while holding ("Keep holding…")
 *   - labelDone — text at 100% fill ("Deleted")
 *   - onConfirm — fires at 100% fill; may return a Promise
 *   - onCancel — fires on early release
 *   - color — 'error' (destructive) | 'warning' | 'primary'
 *   - size, disabled, sx — forwarded to MUI Button
 */
export default function HoldButton({
  holdMs = 900,
  label = 'Hold to confirm',
  labelActive = 'Keep holding…',
  labelDone = 'Confirmed',
  onConfirm,
  onCancel,
  color = 'error',          // 'error' | 'warning' | 'primary'
  size = 'medium',
  disabled = false,
  sx = {},
}) {
  const fillRef = useRef(null)
  const rafRef = useRef(null)
  const startAtRef = useRef(null)
  const [state, setState] = useState('idle')   // 'idle' | 'holding' | 'done'

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
  }, [])

  const paletteBorder = color === 'error' ? V2.errText
                      : color === 'warning' ? V2.warnText : V2.ink
  const paletteFill   = paletteBorder

  const setFill = (pct) => {
    if (fillRef.current) fillRef.current.style.width = `${pct}%`
  }
  const tick = (now) => {
    if (startAtRef.current == null) return
    const pct = Math.min(100, ((now - startAtRef.current) / holdMs) * 100)
    setFill(pct)
    if (pct >= 100) {
      finish()
      return
    }
    rafRef.current = requestAnimationFrame(tick)
  }
  const finish = () => {
    startAtRef.current = null
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null }
    setFill(100)
    setState('done')
    Promise.resolve(onConfirm?.()).finally(() => {
      setTimeout(() => {
        setFill(0)
        setState('idle')
      }, 900)
    })
  }
  const begin = () => {
    if (disabled || state !== 'idle') return
    startAtRef.current = performance.now()
    setState('holding')
    rafRef.current = requestAnimationFrame(tick)
  }
  const cancel = () => {
    if (startAtRef.current == null) return
    startAtRef.current = null
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null }
    setFill(0)
    setState('idle')
    onCancel?.()
  }

  const text = state === 'holding' ? labelActive
             : state === 'done'    ? labelDone
             : label

  const isActive = state !== 'idle'

  return (
    <Button
      variant="outlined"
      color={color}
      size={size}
      disabled={disabled}
      disableRipple
      onPointerDown={(e) => {
        if (e.button != null && e.button !== 0) return
        try { e.currentTarget.setPointerCapture?.(e.pointerId) } catch { /* ignore */ }
        begin()
      }}
      onPointerUp={cancel}
      onPointerCancel={cancel}
      onPointerLeave={cancel}
      onKeyDown={(e) => {
        if ((e.key === ' ' || e.key === 'Enter') && state === 'idle') {
          e.preventDefault()
          begin()
        }
      }}
      onKeyUp={(e) => {
        if (e.key === ' ' || e.key === 'Enter') cancel()
      }}
      onBlur={cancel}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderColor: paletteBorder,
        color: isActive ? '#fff' : paletteBorder,
        transition: 'color 160ms ease',
        WebkitTapHighlightColor: 'transparent',
        WebkitTouchCallout: 'none',
        userSelect: 'none',
        // K-5 (Welle 2): reine Wirkung (Rahmenfarbe) ohne Hintergrund.
        ...hoverOnly({ borderColor: paletteBorder }),
        ...sx,
      }}
    >
      <Box
        ref={fillRef}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '0%',
          bgcolor: paletteFill,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />
      <Box component="span" sx={{ position: 'relative', zIndex: 1, pointerEvents: 'none' }}>
        {text}
      </Box>
    </Button>
  )
}
